import { useState, useMemo } from 'react';
import {
  DollarSign,
  TrendingUp,
  Calendar,
  BarChart2,
  ShieldOff,
  Briefcase,
  Download,
  ChevronLeft,
  ChevronRight,
  Loader2,
} from 'lucide-react';
import { format, startOfMonth, endOfMonth, subMonths } from 'date-fns';
import { UserRole } from '@pms/shared';
import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import { ReservationCalendar } from '@/components/calendar/ReservationCalendar';
import { useOwnerSummary, usePayouts } from '@/hooks/api/useOwner';
import { useSelectedPropertyId } from '@/contexts/PropertyContext';

// ---- Helpers ----

const DEFAULT_CURRENCY = 'USD';

function fmt(value: number, currency: string = DEFAULT_CURRENCY): string {
  return value.toLocaleString('en-US', { style: 'currency', currency, maximumFractionDigits: 0 });
}

// ---- Access Denied Card ----

function AccessDeniedCard() {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-gray-900">Owner Portal</h1>
        <p className="text-sm text-gray-500 mt-0.5">Property performance and financial reports</p>
      </div>
      <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-red-200 bg-white py-24 px-6 text-center gap-4">
        <div className="flex items-center justify-center w-16 h-16 rounded-2xl bg-red-50">
          <ShieldOff size={32} className="text-red-400" aria-hidden="true" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-gray-700">Access Denied</h2>
          <p className="mt-1 text-sm text-gray-400 max-w-sm">This portal is restricted to property owners and administrators.</p>
        </div>
      </div>
    </div>
  );
}

// ---- No Owner Record Card ----

function NoOwnerRecordCard() {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-gray-900">Owner Portal</h1>
        <p className="text-sm text-gray-500 mt-0.5">Property performance and financial reports</p>
      </div>
      <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-200 bg-white py-24 px-6 text-center gap-4">
        <div className="flex items-center justify-center w-16 h-16 rounded-2xl bg-gray-50">
          <Briefcase size={32} className="text-gray-400" aria-hidden="true" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-gray-700">No owner record for this account</h2>
          <p className="mt-1 text-sm text-gray-400 max-w-sm">
            This account is not linked to a property owner, so there is no owner performance data to
            show. The Owner Portal is available to accounts with a linked owner profile.
          </p>
        </div>
      </div>
    </div>
  );
}

// ---- Summary Card ----

function SummaryCard({ label, value, sub, icon, color }: { label: string; value: string; sub?: string; icon: React.ReactNode; color: string }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 flex items-start gap-4">
      <div className={cn('flex items-center justify-center w-11 h-11 rounded-xl shrink-0', color)}>{icon}</div>
      <div className="min-w-0">
        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</p>
        <p className="text-2xl font-bold text-gray-900 mt-0.5 truncate">{value}</p>
        {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

// ---- Revenue Breakdown Table ----

function RevenueBreakdownTable({ summary, currency }: { summary: Record<string, unknown>; currency: string }) {
  const byProperty: Array<Record<string, unknown>> = Array.isArray(summary.byProperty) ? summary.byProperty : [];

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-100">
        <h2 className="text-sm font-semibold text-gray-800">Revenue by Property</h2>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 text-left">
              <th className="px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Property</th>
              <th className="px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide text-right">Bookings</th>
              <th className="px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide text-right">Gross Revenue</th>
              <th className="px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide text-right">Commission</th>
              <th className="px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide text-right">Net Payout</th>
              <th className="px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide text-right">Occupancy</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {byProperty.map((prop) => (
              <tr key={prop.propertyId as string} className="hover:bg-gray-50 transition-colors">
                <td className="px-5 py-3.5 font-medium text-gray-800">{prop.propertyName as string}</td>
                <td className="px-5 py-3.5 text-right text-gray-600">{prop.bookingCount as number}</td>
                <td className="px-5 py-3.5 text-right text-gray-800">{fmt((prop.grossRevenue as number) ?? 0, currency)}</td>
                <td className="px-5 py-3.5 text-right text-red-600">-{fmt((prop.commissionAmount as number) ?? 0, currency)}</td>
                <td className="px-5 py-3.5 text-right font-semibold text-emerald-700">{fmt((prop.netRevenue as number) ?? 0, currency)}</td>
                <td className="px-5 py-3.5 text-right">
                  <span className={cn('inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium', (prop.occupancyPercent as number) >= 75 ? 'bg-emerald-100 text-emerald-700' : (prop.occupancyPercent as number) >= 50 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700')}>
                    {prop.occupancyPercent as number}%
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-gray-50 font-semibold">
              <td className="px-5 py-3.5 text-gray-800">Total</td>
              <td className="px-5 py-3.5 text-right text-gray-800">{(summary.bookingCount as number) ?? 0}</td>
              <td className="px-5 py-3.5 text-right text-gray-800">{fmt((summary.grossRevenue as number) ?? 0, currency)}</td>
              <td className="px-5 py-3.5 text-right text-red-600">-{fmt((summary.commissionAmount as number) ?? 0, currency)}</td>
              <td className="px-5 py-3.5 text-right text-emerald-700">{fmt((summary.netRevenue as number) ?? 0, currency)}</td>
              <td className="px-5 py-3.5 text-right text-gray-600">{(summary.occupancyPercent as number) ?? 0}%</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

// ---- Payout History Table ----

function PayoutHistoryTable({ payouts, currency }: { payouts: Array<Record<string, unknown>>; currency: string }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-100">
        <h2 className="text-sm font-semibold text-gray-800">Payout History</h2>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 text-left">
              <th className="px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Month</th>
              <th className="px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide text-right">Gross Revenue</th>
              <th className="px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide text-right">Commission</th>
              <th className="px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide text-right">Net Payout</th>
              <th className="px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide text-right">Report</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {payouts.map((p) => (
              <tr key={(p.period as string) ?? (p.id as string)} className="hover:bg-gray-50 transition-colors">
                <td className="px-5 py-3.5 font-medium text-gray-800">{(p.label as string) ?? (p.period as string) ?? ''}</td>
                <td className="px-5 py-3.5 text-right text-gray-700">{fmt((p.totalGross as number) ?? 0, currency)}</td>
                <td className="px-5 py-3.5 text-right text-red-600">-{fmt(Math.max(0, ((p.totalGross as number) ?? 0) - ((p.totalNet as number) ?? 0)), currency)}</td>
                <td className="px-5 py-3.5 text-right font-semibold text-emerald-700">{fmt((p.totalNet as number) ?? 0, currency)}</td>
                <td className="px-5 py-3.5 text-right">
                  <button className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500">
                    <Download size={12} aria-hidden="true" />
                    PDF
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---- Date Range Picker ----

function MonthRangePicker({ from, onChange }: { from: Date; to: Date; onChange: (from: Date, to: Date) => void }) {
  const label = format(from, 'MMMM yyyy');
  function handlePrev() { const prev = subMonths(from, 1); onChange(startOfMonth(prev), endOfMonth(prev)); }
  function handleNext() { const next = new Date(from.getFullYear(), from.getMonth() + 1, 1); onChange(startOfMonth(next), endOfMonth(next)); }

  return (
    <div className="flex items-center gap-2">
      <button onClick={handlePrev} aria-label="Previous month" className="p-1.5 rounded-lg border border-gray-300 text-gray-500 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 transition-colors">
        <ChevronLeft size={16} aria-hidden="true" />
      </button>
      <span className="text-sm font-medium text-gray-700 min-w-[130px] text-center">{label}</span>
      <button onClick={handleNext} aria-label="Next month" className="p-1.5 rounded-lg border border-gray-300 text-gray-500 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 transition-colors">
        <ChevronRight size={16} aria-hidden="true" />
      </button>
    </div>
  );
}

// ---- Main Page ----

export function OwnerDashboardPage() {
  const { user } = useAuth();
  const today = useMemo(() => new Date(), []);
  const [rangeFrom, setRangeFrom] = useState<Date>(() => startOfMonth(today));
  const [rangeTo, setRangeTo] = useState<Date>(() => endOfMonth(today));

  // Only OWNER accounts have a linked owner record and can load owner data.
  // Admins may open the page (e.g. via a bookmarked URL) but have no owner
  // record, so we never fire the owner requests for them (avoids 404 noise) and
  // show a clear "no owner record" state instead of an infinite spinner.
  const isOwner = user?.role === UserRole.OWNER;
  const canView = isOwner || user?.role === UserRole.ADMIN;
  const propertyId = useSelectedPropertyId();

  const { data: summaryData, isLoading: summaryLoading, isError: summaryError } = useOwnerSummary(
    {
      from: format(rangeFrom, 'yyyy-MM-dd'),
      to: format(rangeTo, 'yyyy-MM-dd'),
      ...(propertyId ? { propertyId } : {}),
    },
    isOwner,
  );
  const { data: payoutsData, isLoading: payoutsLoading } = usePayouts(isOwner);

  const summary: Record<string, unknown> = summaryData ?? {};
  const summaryCurrency = (summary.currency as string) ?? DEFAULT_CURRENCY;
  const payouts: Array<Record<string, unknown>> = useMemo(() => {
    if (!payoutsData) return [];
    return Array.isArray(payoutsData) ? payoutsData : (payoutsData.data ?? []);
  }, [payoutsData]);

  const ownerName = user?.email?.split('@')[0] ?? 'Owner';

  if (!canView) return <AccessDeniedCard />;
  // Admins (and any owner whose record can't be resolved) get a clear empty
  // state rather than a hung spinner.
  if (!isOwner || summaryError) return <NoOwnerRecordCard />;

  function handleRangeChange(from: Date, to: Date) {
    setRangeFrom(from);
    setRangeTo(to);
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Owner Portal</h1>
          <p className="text-sm text-gray-500 mt-0.5">Welcome back, {ownerName}</p>
        </div>
        <MonthRangePicker from={rangeFrom} to={rangeTo} onChange={handleRangeChange} />
      </div>

      {summaryLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 size={24} className="animate-spin text-primary-600" aria-hidden="true" />
          <span className="ml-2 text-sm text-gray-500">Loading summary...</span>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <SummaryCard label="Total Revenue" value={fmt((summary.grossRevenue as number) ?? 0, summaryCurrency)} sub={format(rangeFrom, 'MMMM yyyy')} icon={<DollarSign size={20} className="text-blue-600" aria-hidden="true" />} color="bg-blue-50" />
            <SummaryCard label="Net Payout" value={fmt((summary.netRevenue as number) ?? 0, summaryCurrency)} sub="After commission" icon={<TrendingUp size={20} className="text-emerald-600" aria-hidden="true" />} color="bg-emerald-50" />
            <SummaryCard label="Occupancy" value={`${(summary.occupancyPercent as number) ?? 0}%`} sub="Across all units" icon={<BarChart2 size={20} className="text-violet-600" aria-hidden="true" />} color="bg-violet-50" />
            <SummaryCard label="Bookings" value={String((summary.bookingCount as number) ?? 0)} sub={format(rangeFrom, 'MMMM yyyy')} icon={<Calendar size={20} className="text-amber-600" aria-hidden="true" />} color="bg-amber-50" />
          </div>

          <RevenueBreakdownTable summary={summary} currency={summaryCurrency} />
        </>
      )}

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100">
          <h2 className="text-sm font-semibold text-gray-800">Booking Calendar</h2>
          <p className="text-xs text-gray-400 mt-0.5">Read-only view of all reservations</p>
        </div>
        <div className="h-[420px]">
          <ReservationCalendar />
        </div>
      </div>

      {payoutsLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 size={20} className="animate-spin text-primary-600" />
          <span className="ml-2 text-sm text-gray-500">Loading payouts...</span>
        </div>
      ) : (
        <PayoutHistoryTable payouts={payouts} currency={summaryCurrency} />
      )}
    </div>
  );
}
